import { FC, Fragment } from 'react'
import { SettingsPageLayout } from '../components/SettingsPageLayout'
import { useQuery } from '@sequencehq/api'
import {
  BASE_INPUT_FIELD_STYLES,
  SETTINGS_STALE_TIME
} from '../settings.constants'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import {
  Text,
  SettingsFormControl,
  Switch,
  SettingsGroup,
  Button,
  TextInputField,
  GreyGrey30,
  BorderRadius8,
  GreyGrey20,
  GreyGrey70,
  Primary13Regular,
  IconCircleMinus,
  IconPlus,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'

import { useESignatureSettings } from 'SettingsV2/components/e-signature/useESignatureSettings'
import { isEmail, required } from '@sequencehq/utils/validation'
import { LogoDropboxSign } from 'SettingsV2/components/e-signature/LogoDropboxSign'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const QuotesSettingsPage: FC = () => {
  const { useQuotePortalAuth } = useFeatureFlags()
  const { data, isError, isLoading } = useQuery(
    dashboardv20240509Client.getQuoteSettings,
    undefined,
    {
      staleTime: SETTINGS_STALE_TIME
    }
  )

  const {
    queries,
    fields,
    isPending,
    updateESignatureEnable,
    addCounterSigner,
    removeCounterSigner,
    clearCounterSigners,
    updateCounterSigner,
    showValidationErrors,
    setShowValidationErrors,
    updateIsAutoExecuteQuotesEnabled,
    updateNotifyCounterSignerLast,
    updateCustomerPortalAuthRequired
  } = useESignatureSettings(data)

  const counterSigners = queries.formData.counterSigners ?? []

  const showRemoveButtonColumn: boolean = counterSigners.length > 1

  return (
    <SettingsPageLayout
      header="Quotes"
      subheader="Configure how quotes are signed and who can view them."
      isError={isError}
    >
      <Flex direction="column" gap="16px">
        <SettingsGroup title="E-signature" isLoading={isLoading}>
          <SettingsFormControl
            label="Enable e-signature"
            htmlFor="enable-e-signature"
          >
            <Switch
              id="enable-e-signature"
              data-testid="settings.eSignature.enableSwitch"
              as="div"
              size="md"
              disabled={isPending}
              isChecked={fields.isSignaturesEnabled.value}
              onChange={(value: boolean) => {
                fields.isSignaturesEnabled.onChange(value)
                updateESignatureEnable(value)
              }}
            />
          </SettingsFormControl>

          {queries.formData.isSignaturesEnabled && (
            <>
              <SettingsFormControl
                label="Enable countersigners"
                helper="Automatically include a coworker to countersign every quote"
                htmlFor="enable-countersigner"
                showBorderBottom={!fields.isCounterSignersEnabled.value}
              >
                <Switch
                  id="enable-countersigner"
                  data-testid="settings.eSignature.counterSignersSwitch"
                  size="md"
                  as="div"
                  disabled={isPending}
                  isChecked={fields.isCounterSignersEnabled.value}
                  onChange={(value: boolean) => {
                    fields.isCounterSignersEnabled.onChange(value)

                    if (value) {
                      addCounterSigner()

                      /**
                       * the switch regains focus when enabled, stopping the autofocus on the countersigner name input
                       * from working
                       */
                      setTimeout(() => {
                        document.getElementById('name.new')?.focus()
                      }, 1)
                    } else {
                      clearCounterSigners()
                    }
                  }}
                />
              </SettingsFormControl>

              {queries.formData.isCounterSignersEnabled && (
                <>
                  <Grid
                    gridTemplateColumns={
                      showRemoveButtonColumn ? '1fr 1fr 32px' : '1fr 1fr'
                    }
                    columnGap="8px"
                    rowGap="16px"
                  >
                    {counterSigners.map((counterSigner, index) => {
                      const nameError = required(counterSigner.name)
                      const emailError = isEmail(counterSigner.email)

                      const isInvalid =
                        !queries.isDirty ||
                        !queries.isValid ||
                        !counterSigner.name ||
                        !counterSigner.email ||
                        emailError ||
                        nameError

                      // prevent removing the only countersigner when an empty row for a new countersigner is added
                      const showRemoveButton =
                        index !== 0 ||
                        (index === 0 && data?.counterSigners?.at(1))

                      return (
                        <Fragment key={counterSigner.id}>
                          <GridItem>
                            <TextInputField
                              {...BASE_INPUT_FIELD_STYLES}
                              value={counterSigner.name}
                              validationErrors={
                                showValidationErrors && nameError
                                  ? [{ message: nameError }]
                                  : undefined
                              }
                              onChange={e => {
                                fields.counterSigners.onChange(
                                  counterSigners.map(p =>
                                    p.id === counterSigner.id
                                      ? { ...p, name: e.target.value }
                                      : p
                                  )
                                )
                              }}
                              disabled={isPending}
                              placeholder="John Doe"
                              id={`name.${counterSigner.id}`}
                              autoFocus={counterSigner.id === 'new'}
                              width="100%"
                              onBlur={() => {
                                if (counterSigner.name && counterSigner.email) {
                                  setShowValidationErrors(true)
                                }

                                if (isInvalid) return

                                updateCounterSigner()
                              }}
                              data-testid={`countersigners[${index}].name`}
                            />
                          </GridItem>
                          <GridItem>
                            <TextInputField
                              {...BASE_INPUT_FIELD_STYLES}
                              value={counterSigner.email}
                              onChange={e => {
                                fields.counterSigners.onChange(
                                  counterSigners.map(p =>
                                    p.id === counterSigner.id
                                      ? { ...p, email: e.target.value }
                                      : p
                                  )
                                )
                              }}
                              validationErrors={
                                showValidationErrors && emailError
                                  ? [{ message: emailError }]
                                  : undefined
                              }
                              onBlur={() => {
                                if (counterSigner.name && counterSigner.email) {
                                  setShowValidationErrors(true)
                                }

                                if (isInvalid) return

                                updateCounterSigner()
                              }}
                              disabled={isPending}
                              placeholder="john@acme.co"
                              id={`email.${counterSigner.id}`}
                              width="100%"
                              type="email"
                              data-testid={`countersigners[${index}].email`}
                            />
                          </GridItem>
                          {showRemoveButtonColumn && (
                            <GridItem>
                              {showRemoveButton && (
                                <Button
                                  variant="ghost"
                                  icon={<IconCircleMinus color={GreyGrey70} />}
                                  onClick={() =>
                                    removeCounterSigner(counterSigner.id)
                                  }
                                  aria-label="Remove countersigner"
                                  data-testid={`countersigners[${index}].remove`}
                                />
                              )}
                            </GridItem>
                          )}
                        </Fragment>
                      )
                    })}
                  </Grid>

                  <Flex gap="18px" marginTop="18px" flexDirection="column">
                    <Button
                      data-testid="settings.eSignature.addCounterSigner"
                      variant="secondary"
                      leadingIcon={<IconPlus />}
                      style={{ width: 'fit-content' }}
                      disabled={
                        counterSigners.at(-1)?.id === 'new' ||
                        !queries.isValid ||
                        isPending
                      }
                      onClick={() => addCounterSigner()}
                    >
                      Add countersigner
                    </Button>

                    <Box
                      width="100%"
                      height="1px"
                      backgroundColor={GreyGrey30}
                    />
                  </Flex>

                  <SettingsFormControl
                    label="Notify countersigners last"
                    helper="Only send signature requests after everyone else has signed"
                    htmlFor="enable-notify-countersigner-last"
                  >
                    <Switch
                      id="enable-notify-countersigner-last"
                      data-testid="settings.eSignature.notifyCounterSignerLastSwitch"
                      size="md"
                      as="div"
                      disabled={isPending || !data?.counterSigners?.at(0)}
                      isChecked={fields.notifyCounterSignerLast.value}
                      onChange={(value: boolean) => {
                        updateNotifyCounterSignerLast(value)
                      }}
                    />
                  </SettingsFormControl>
                </>
              )}

              <SettingsFormControl
                label="Automatically execute accepted quotes"
                helper="Creates a draft of a billing schedule once everyone has signed"
                htmlFor="auto-execute-quotes"
              >
                <Switch
                  id="auto-execute-quotes"
                  data-testid="settings.eSignature.autoExecuteSwitch"
                  size="md"
                  as="div"
                  disabled={isPending}
                  isChecked={Boolean(data?.isAutoExecuteQuotesEnabled)}
                  onChange={(value: boolean) => {
                    updateIsAutoExecuteQuotesEnabled(value)
                  }}
                />
              </SettingsFormControl>
            </>
          )}
        </SettingsGroup>

        {useQuotePortalAuth && (
          <SettingsGroup
            title="Authentication"
            isLoading={isLoading}
            footer={
              <SettingsGroup.Footer>
                When enabled, quote recipients must verify their identity before
                viewing quotes.
              </SettingsGroup.Footer>
            }
          >
            <SettingsFormControl
              label="Require authentication to access quotes"
              htmlFor="require-quote-authentication"
            >
              <Switch
                id="require-quote-authentication"
                data-testid="settings.quotes.portalAuthSwitch"
                as="div"
                size="md"
                disabled={isPending}
                isChecked={data?.customerPortalAuthRequired === 'REQUIRED'}
                onChange={updateCustomerPortalAuthRequired}
              />
            </SettingsFormControl>
          </SettingsGroup>
        )}

        <Flex
          justifyContent="space-between"
          backgroundColor={GreyGrey20}
          borderRadius={BorderRadius8}
          alignItems="center"
          padding="12px"
          marginTop="16px"
        >
          <Text as="span" color={GreyGrey70} {...Primary13Regular}>
            Powered by
          </Text>
          <LogoDropboxSign height={24} width={120} />
        </Flex>
      </Flex>
    </SettingsPageLayout>
  )
}
